import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { IMooring, IPosition } from '../generated-types'
import { MooringDropdownDto } from '../generated-types/mooring-drop-down-dto'
import { IMooringsRequestDto } from '../generated-types/moorings-request-dto'
import { queryClient } from '../query-client'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { mooringQueryKeys } from 'src/query-keys'
import { usePatch } from '@lovoldsystem/core.client'
import {
  PositionGroupsWithDrawing,
  PositionGroup,
  MooringDrawingGroupsResult,
  ExternalComponentsDto,
} from 'src/generated-types/mooring-position-drawing-group'
import { IConfigureMooringPositionsData } from 'src/generated-types/configure-mooring-positions'
import { IMooringMapOutlineDto } from 'src/generated-types/mooring-map-outline-dto'

const baseUrl = (customerId: EntityId) => `customers/${customerId}`

export const mooringsEndpoints = {
  getMoorings(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings`
  },
  getArchivedMoorings(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/archived-moorings`
  },
  getMooringsForMap(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings-for-map`
  },
  getMooringOutlinesForMap(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/outlines-for-map`
  },
  getMooringsForService(customerId: EntityId, serviceId: EntityId) {
    return `${baseUrl(customerId)}/services/${serviceId}/getAllForService`
  },
  getMooring(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/get-mooring`
  },
  createMooring(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings`
  },
  updateMooring(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}`
  },
  restoreMooring(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/restore`
  },
  updatePositionTemplates(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/position-templates`
  },
  deleteMooring(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings`
  },
  getMapSvg(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/map-svg`
  },
  archiveMooring(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/archive`
  },
  getAllMooringsByLocalityId(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/get-all-moorings`
  },
  getSvgForPosition(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/get-svg-for-position`
  },

  regeneratePositions(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${id}/regenerate-positions`
  },
  updatePositionDrawingsOfaMooring(customerId, mooringId) {
    return `${baseUrl(customerId)}/moorings/${mooringId}/update-position-drawings-for-mooring`
  },
  exportOlex(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/export-olex`
  },
  uploadMooringOlex(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/upload-olex`
  },
  getMooringDataForOlexUpload(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/get-data-for-olex-upload`
  },
  // Position grouping endpoints
  getAllPositionDrawingGroups(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/positions/all-groups`
  },
  getPositionsGroupedByDrawingGroups(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/positions/groups-with-drawing`
  },
  getExternalComponents(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/external-components`
  },
  bulkConfigurePositions(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/positions/bulk-configure-positions`
  },
  renamePositionGroup(customerId: EntityId, localityId: EntityId, mooringId: EntityId, groupId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/positions/position-groups/${groupId}`
  },
  deletePositionDrawingGroup(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/localities/${localityId}/moorings/${mooringId}/positions/position-drawing-groups`
  },
  getPositionGroupsForDrawing(
    customerId: EntityId,
    localityId: EntityId,
    mooringId: EntityId,
    positionDrawingId: EntityId,
  ) {
    return `${baseUrl(customerId)}/moorings/${mooringId}/position-drawings/${positionDrawingId}/get-position-groups`
  },
}

export const invalidateMooringCache = {
  getMooringsForService(customerId: EntityId, serviceId: EntityId) {
    queryClient.invalidateQueries(mooringsEndpoints.getMooringsForService(customerId, serviceId))
  },
  getGetMooring(
    customerId?: EntityId,
    localityId?: EntityId,
    id?: EntityId,
    queryParams?: { isWithAllInfo?: boolean; isWithPositionData?: boolean },
  ) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_MOORING(customerId, localityId, id, queryParams))
  },
  getGetArchivedMoorings(customerId: EntityId, localityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_ARCHIVED_MOORINGS(customerId, localityId))
  },
  getGetMoorings(
    customerId: EntityId,
    localityId: EntityId,
    isWithPositionData?: boolean,
    page: number = apiConsts.Page,
    pageSize: number = apiConsts.PageSize,
  ) {
    queryClient.invalidateQueries([
      mooringsEndpoints.getMoorings(customerId, localityId),
      {
        isWithPositionData,
        page,
        pageSize,
      },
    ])
  },
  getMapSvg(customerId?: EntityId, localityId?: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_MAP_SVG(customerId, localityId, id))
  },
  getSvgForPosition(
    customerId?: EntityId,
    localityId?: EntityId,
    mooringId?: EntityId,
    queryParams?: { positionId: number },
  ) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_SVG_FOR_POSITION(customerId, localityId, mooringId, queryParams))
  },
  getMooringsForMap(customerId?: EntityId, localityId?: EntityId, queryParams?: IMooringsRequestDto) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_MOORINGS_FOR_MAP(customerId, localityId, queryParams))
  },
  getMooringOutlinesForMap(customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_MOORING_OUTLINES_FOR_MAP(customerId, localityId, mooringId))
  },
  getAllMooringsByLocalityId(customerId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_MOORING_FOR_DROPDOWN(customerId, localityId))
  },
  getMooringDataForOlexUpload(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(mooringsEndpoints.getMooringDataForOlexUpload(customerId, localityId, mooringId))
  },
  // Position grouping cache invalidation
  getAllPositionGroups(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_ALL_POSITION_GROUPS(customerId, localityId, mooringId))
  },
  getPositionGroupsWithDrawing(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(mooringQueryKeys.GET_POSITION_GROUPS_WITH_DRAWING(customerId, localityId, mooringId))
  },
  getPositionGroupsForDrawing(
    customerId: EntityId,
    localityId: EntityId,
    mooringId: EntityId,
    positionDrawingId?: EntityId,
  ) {
    queryClient.invalidateQueries(
      mooringQueryKeys.GET_POSITION_GROUPS_FOR_DRAWING(customerId, localityId, mooringId, positionDrawingId),
    )
  },
  getExternalComponentsForMooring(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries(
      mooringQueryKeys.GET_EXTERNAL_COMPONENTS_FOR_MOORING(customerId, localityId, mooringId),
    )
  },
}

export const invalidateMooringMapCaches = (customerId?: EntityId, localityId?: EntityId, mooringId?: EntityId) => {
  invalidateMooringCache.getGetMooring(customerId, localityId, mooringId)
  invalidateMooringCache.getMooringsForMap(customerId, localityId)
  invalidateMooringCache.getMooringOutlinesForMap(customerId, localityId)
  invalidateMooringCache.getMapSvg(customerId, localityId, mooringId)
  invalidateMooringCache.getSvgForPosition(customerId, localityId, mooringId)
}

export const useGetMoorings = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams?: IMooringsRequestDto,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<IPaginatedResult<IMooring>>(mooringsEndpoints.getMoorings(customerId, localityId), {
    ...queryParams,
    page,
    pageSize,
  })

export const useGetArchivedMoorings = (customerId: EntityId, localityId: EntityId) =>
  useGet<Array<IMooring>>(mooringsEndpoints.getArchivedMoorings(customerId, localityId), undefined, {
    queryKey: mooringQueryKeys.GET_ARCHIVED_MOORINGS(customerId, localityId),
  })

export const useGetMooringsForMap = (customerId: EntityId, localityId: EntityId, queryParams?: IMooringsRequestDto) =>
  useGet<Array<IMooring>>(
    mooringsEndpoints.getMooringsForMap(customerId, localityId),
    {
      ...queryParams,
    },
    { queryKey: mooringQueryKeys.GET_MOORINGS_FOR_MAP(customerId, localityId, queryParams) },
  )

export const useGetMooringOutlinesForMap = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<Array<IMooringMapOutlineDto>>(
    mooringsEndpoints.getMooringOutlinesForMap(customerId, localityId, mooringId),
    {},
    { queryKey: mooringQueryKeys.GET_MOORING_OUTLINES_FOR_MAP(customerId, localityId, mooringId) },
  )

export const useGetMooringsForService = (customerId: EntityId, serviceId: EntityId, page?: number, pageSize?: number) =>
  useGet<IPaginatedResult<IMooring>>(mooringsEndpoints.getMooringsForService(customerId, serviceId), {
    page,
    pageSize,
  })

export const useGetMooring = (
  customerId: EntityId,
  localityId: EntityId,
  id: EntityId,
  queryParams?: { isWithAllInfo?: boolean; isWithPositionData?: boolean; isIgnoreQueryFilters?: boolean },
) =>
  useGet<IMooring>(
    mooringsEndpoints.getMooring(customerId, localityId, id),
    { ...queryParams },
    {
      enabled: id != undefined ? true : false,
      queryKey: mooringQueryKeys.GET_MOORING(
        customerId,
        localityId != null ? localityId : undefined,
        id != null ? id : undefined,
        queryParams,
      ),
    },
  )

export const useAddMooring = (customerId: EntityId, localityId: EntityId) =>
  usePost<IMooring, IMooring>(mooringsEndpoints.createMooring(customerId, localityId))

export const useUpdatePositionDrawingsOfaMooring = (customerId: EntityId, mooringId: EntityId) =>
  usePost<IMooring, { positionDrawings: IPositionDrawing[]; initialPositionDrawings?: IPositionDrawing[] }>(
    mooringsEndpoints.updatePositionDrawingsOfaMooring(customerId, mooringId),
  )

export const useUpdateMooring = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<IMooring, IMooring>(mooringsEndpoints.updateMooring(customerId, localityId, id))

export const useRestoreMooring = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePatch<IMooring, undefined>(mooringsEndpoints.restoreMooring(customerId, localityId, id))

export const useRenamePositionGroup = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  groupId: EntityId,
) => usePatch<void, { name: string }>(mooringsEndpoints.renamePositionGroup(customerId, localityId, mooringId, groupId))

export const useArchiveMooring = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<IMooring, IMooring>(mooringsEndpoints.archiveMooring(customerId, localityId, id))

export const useUpdatePositionTemplates = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<any, { positionIdList: number[]; positionDrawingId: number }>(
    mooringsEndpoints.updatePositionTemplates(customerId, localityId, id),
  )

export const useDeleteMooring = (customerId: EntityId, localityId: EntityId) =>
  useDelete<IMooring>(mooringsEndpoints.deleteMooring(customerId, localityId))

export const useGetMapSvg = (customerId: EntityId, localityId: EntityId, id: EntityId, enableNavigation = false) =>
  useGet<string>(
    mooringsEndpoints.getMapSvg(customerId, localityId, id),
    { enableNavigation: enableNavigation },
    {
      enabled: id != null && (id as number) > 0,
      queryKey: mooringQueryKeys.GET_MAP_SVG(customerId, localityId, id),
    },
  )

export const useGetSvgForPosition = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  queryParams?: { positionId: number },
) =>
  useGet<string>(
    mooringsEndpoints.getSvgForPosition(customerId, localityId, mooringId),
    { ...queryParams },
    { queryKey: mooringQueryKeys.GET_SVG_FOR_POSITION(customerId, localityId, mooringId, queryParams) },
  )

export const useRegeneratePositions = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<IMooring, IMooring>(mooringsEndpoints.regeneratePositions(customerId, localityId, id))

export const useGetAllMooringsByLocalityId = (customerId: EntityId, localityId: EntityId) =>
  useGet<Array<MooringDropdownDto>>(mooringsEndpoints.getAllMooringsByLocalityId(customerId, localityId), undefined, {
    enabled: localityId != null && (localityId as number) > 0,
    queryKey: mooringQueryKeys.GET_MOORING_FOR_DROPDOWN(
      customerId,
      localityId != null && (localityId as number) > 0 ? localityId : undefined,
    ),
  })

export const useExportMooringOlex = (customerId: EntityId, localityId: EntityId) => {
  return usePost<any, { mooringId: EntityId }>(
    mooringsEndpoints.exportOlex(customerId, localityId),
    {},
    { responseType: 'blob' },
  )
}

export const useUploadMooringOlex = (customerId: EntityId, localityId: EntityId) =>
  usePost<IPosition[], FormData>(mooringsEndpoints.uploadMooringOlex(customerId, localityId))

export const useGetMooringDataForOlexUpload = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<any>(mooringsEndpoints.getMooringDataForOlexUpload(customerId, localityId, mooringId))

// Position grouping hooks
export const useGetAllPositionGroups = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) => {
  return useGet<PositionGroup[]>(
    mooringsEndpoints.getAllPositionDrawingGroups(customerId, localityId, mooringId),
    {},
    {
      queryKey: mooringQueryKeys.GET_ALL_POSITION_GROUPS(customerId, localityId, mooringId),
    },
  )
}

export const useGetPositionGroupsWithDrawing = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) => {
  return useGet<PositionGroupsWithDrawing[]>(
    mooringsEndpoints.getPositionsGroupedByDrawingGroups(customerId, localityId, mooringId),
    {},
    {
      queryKey: mooringQueryKeys.GET_POSITION_GROUPS_WITH_DRAWING(customerId, localityId, mooringId),
    },
  )
}

export const useGetExternalComponentsForMooring = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<ExternalComponentsDto>(
    mooringsEndpoints.getExternalComponents(customerId, localityId, mooringId),
    {},
    {
      queryKey: mooringQueryKeys.GET_EXTERNAL_COMPONENTS_FOR_MOORING(customerId, localityId, mooringId),
    },
  )

export const useBulkConfigureMooringPositions = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  usePost<void, IConfigureMooringPositionsData[]>(
    mooringsEndpoints.bulkConfigurePositions(customerId, localityId, mooringId),
  )

export const useDeletePositionGroup = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useDelete(mooringsEndpoints.deletePositionDrawingGroup(customerId, localityId, mooringId))

export const useGetPositionGroupsForDrawing = (
  customerId: EntityId,
  localityId: EntityId,
  mooringId: EntityId,
  positionDrawingId: EntityId,
) =>
  useGet<MooringDrawingGroupsResult>(
    mooringsEndpoints.getPositionGroupsForDrawing(customerId, localityId, mooringId, positionDrawingId),
    {},
    {
      queryKey: mooringQueryKeys.GET_POSITION_GROUPS_FOR_DRAWING(customerId, localityId, mooringId, positionDrawingId),
      enabled: positionDrawingId != null && (positionDrawingId as number) > 0,
    },
  )
